import * as MapLibre from 'maplibre-gl';

import {
    gameDirectionToBearing as calculateGameDirectionBearing,
    gameCoordinatesToLngLat,
    getLngLatBearing,
    lngLatToGameCoordinates,
    remapGameCoordinates,
} from './coordinate-math';
import { ICONS } from './map-icons';

export function isMapReady(map: MapLibre.Map | null): map is MapLibre.Map {
    return map != null && (map as MapLibre.Map & { _removed?: boolean })._removed !== true;
}

import type {
    MapDataGameCoordinateDeltas,
    MapDataGameCoordinates,
    MapDataLocationFeature,
    MapDataRegionGeometry,
    PlayerLocationReferencePoint,
} from './map-types';

const DEFAULT_FONT_INFO = ICONS.fonts.find(i => i.default) as MapFontInfo;

export const DEFAULT_PIN_ICON = 'map-pin';

interface MapFontInfo {
    fontName: string;
    glyphPrefix: string;
    dynamic?: boolean;
    default?: boolean;
}

export function getFontInfoForGlyph(glyph: string): MapFontInfo {
    if (!glyph) {
        return DEFAULT_FONT_INFO;
    }

    const builtInFont = ICONS.fonts.find(i => i.glyphPrefix && glyph.startsWith(i.glyphPrefix));
    if (builtInFont) {
        return builtInFont as MapFontInfo;
    }

    const separator = glyph.indexOf(':');
    const namespace = separator > 0 ? glyph.slice(0, separator) : '';
    if (/^[a-z0-9][a-z0-9-]{0,63}$/.test(namespace)) {
        return { fontName: namespace, glyphPrefix: `${namespace}:`, dynamic: true };
    }

    return DEFAULT_FONT_INFO;
}

export function getIconForGlyph(glyph: string): string {
    const fontInfo = getFontInfoForGlyph(glyph);
    if (fontInfo.dynamic) {
        const value = glyph.slice(fontInfo.glyphPrefix.length);
        const codepoint = /^U\+([0-9A-F]{4,6})$/i.exec(value);
        if (codepoint?.[1]) {
            const number = Number.parseInt(codepoint[1], 16);
            if (number <= 0x10ffff && (number < 0xd800 || number > 0xdfff)) {
                return String.fromCodePoint(number);
            }
        }
        return value || ICONS.glyphs[DEFAULT_PIN_ICON];
    }

    return ICONS?.glyphs?.[glyph as keyof typeof ICONS.glyphs] ?? ICONS.glyphs[DEFAULT_PIN_ICON];
}

/**
 * Returns the appropriate font family style for a given font name
 * @param fontName - The name of the font
 * @returns Styles required to render the icon font
 */
export function getSubcategoryIconStyle(fontName: string): { fontFamily: string; whiteSpace: 'nowrap' } {
    return { fontFamily: fontName || DEFAULT_FONT_INFO.fontName, whiteSpace: 'nowrap' };
}

export const R = 6371e3;

export function latLonToXYWebMercator(lat: number, lon: number): { x: number; y: number } {
    const x = (R * lon * Math.PI) / 180;
    const y = R * Math.log(Math.tan(((90 + lat) * Math.PI) / 360));

    return { x, y };
}

// https://gist.github.com/onderaltintas/6649521

export function degreesToMeters(lat: number, lon: number): [number, number] {
    const x = (lon * 20037508.34) / 180;
    const y = ((Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180)) * 20037508.34) / 180;

    return [y, x];
}

export function metersToDegrees(x: number, y: number): [number, number] {
    const lon = (y * 180) / 20037508.34;
    const lat = (Math.atan(Math.exp((x * Math.PI) / 20037508.34)) * 360) / Math.PI - 90;

    return [lat, lon];
}

export const findClosestPoint = (
    points: MapLibre.MapGeoJSONFeature[],
    clickedLngLat: { lng: number; lat: number },
): MapLibre.MapGeoJSONFeature | undefined => {
    const clicked = MapLibre.MercatorCoordinate.fromLngLat(clickedLngLat);

    if (points.length === 0) {
        return undefined;
    }

    const closestPoint = points.reduce(
        (closest: { distance: number; feature?: MapLibre.MapGeoJSONFeature }, point) => {
            if (point.geometry.type === 'Point') {
                const [lng, lat] = point.geometry.coordinates as [number, number];
                const projected = MapLibre.MercatorCoordinate.fromLngLat({ lng, lat });

                const dx = projected.x - clicked.x;
                const dy = projected.y - clicked.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                return distance < closest.distance ? { distance, feature: point } : closest;
            }

            return closest;
        },
        { distance: Infinity },
    );

    return closestPoint.feature;
};

/**
 * Equirectangular (cosine-corrected) distance between two lng/lat points.
 *
 * Flat Euclidean on raw lng/lat overstates distance along the longitude axis at
 * non-equator latitudes, because 1° of longitude covers less screen space than 1° of
 * latitude once Mercator projection is applied. Multiplying the longitude delta by
 * `cos(avg_lat)` normalizes the two axes so the result tracks visual/screen distance
 * on the rendered Mercator map. Accurate for the small radii used in nearby-pin
 * detection; for long-distance work use a true great-circle (haversine) formula.
 */
export function calculateMercatorAwareDistance(
    point1: { lng: number; lat: number },
    point2: { lng: number; lat: number },
): number {
    const avgLatRad = (((point1.lat + point2.lat) / 2) * Math.PI) / 180;
    const dLng = (point1.lng - point2.lng) * Math.cos(avgLatRad);
    const dLat = point1.lat - point2.lat;
    return Math.hypot(dLng, dLat);
}

export function getNearbyPins(config: {
    points: readonly MapDataLocationFeature[];
    referencePoint: MapDataLocationFeature;
    proximityLevel: number; // 1 - extremely close, 2 = close, etc.
}): MapDataLocationFeature[] {
    const { points, referencePoint, proximityLevel } = config;

    const proximityRadii = [
        0.5, // 1: almost stacked
        1, // 2: very close
        2, // 3: close
        4, // 4: nearby
        8, // 5: short walk
        16, // 6: block
        32, // 7: neighborhood
        64, // 8: city
        128, // 9: region
        256, // 10: essentially all pins
    ];

    // Clamp proximityLevel to 1-10
    const level = Math.max(1, Math.min(10, proximityLevel));
    const thresholdUnits = proximityRadii[level - 1] ?? 256;

    if (
        referencePoint.geometry.type !== 'Point' ||
        !referencePoint.geometry.coordinates ||
        referencePoint.geometry.coordinates.length < 2
    ) {
        return [];
    }

    const refCoords = referencePoint.geometry.coordinates;
    const refLng = refCoords[0];
    const refLat = refCoords[1];

    if (typeof refLng !== 'number' || typeof refLat !== 'number') {
        return [];
    }

    const refExternalId = referencePoint.properties.externalId;
    const matches: Array<{ pin: MapDataLocationFeature; distance: number }> = [];
    for (const point of points) {
        if (point.geometry.type !== 'Point') continue;
        if (point.properties.externalId === refExternalId) continue;
        const coords = point.geometry.coordinates;
        if (!coords || coords.length < 2 || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') {
            continue;
        }
        const [lng, lat] = coords;
        const distance = calculateMercatorAwareDistance({ lat: refLat, lng: refLng }, { lat, lng });
        if (distance <= thresholdUnits) matches.push({ pin: point, distance });
    }
    matches.sort((a, b) => a.distance - b.distance);
    return matches.map(m => m.pin);
}

export function remapGameCoords(
    gameCoords: MapDataGameCoordinates,
    gameCoordsMap: MapDataGameCoordinateDeltas['gameCoordsMap'],
): MapDataGameCoordinates {
    return remapGameCoordinates(gameCoords, gameCoordsMap);
}

export function gameDirectionToBearing(
    direction: MapDataGameCoordinates,
    deltas: MapDataGameCoordinateDeltas,
): number | null {
    return calculateGameDirectionBearing(direction, deltas);
}

export function gameCoordsToLatLng(
    gameCoords: MapDataGameCoordinates,
    deltas: MapDataGameCoordinateDeltas,
): MapLibre.LngLat | null {
    if (
        !gameCoords ||
        typeof gameCoords.x !== 'number' ||
        typeof gameCoords.y !== 'number' ||
        !Number.isFinite(gameCoords.x) ||
        !Number.isFinite(gameCoords.y)
    ) {
        return null;
    }

    const point = gameCoordinatesToLngLat(gameCoords, deltas);
    return point ? new MapLibre.LngLat(point.lng, point.lat) : null;
}

/**
 * Convert lat/lng map coordinates back to game-coordinate space
 * (inverse of gameCoordsToLatLng).
 */
export function latLngToGameCoords(
    lng: number,
    lat: number,
    deltas: MapDataGameCoordinateDeltas,
): { x: number; y: number } | null {
    return lngLatToGameCoordinates({ lng, lat }, deltas);
}

/** 2D Euclidean distance (X/Y plane, ignoring elevation). */
export function gameDistance2D(a: { x: number; y: number }, b: { x: number; y: number }): number {
    return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

export function calculateBearing(from: MapLibre.LngLat, to: MapLibre.LngLat): number {
    return getLngLatBearing(from, to);
}

export function clampCoordinatesToBounds(
    lngLat: MapLibre.LngLat,
    map: MapLibre.Map,
    paddingPx: number = 25,
): {
    clampedLngLat: MapLibre.LngLat;
    isClamped: boolean;
} {
    const mapContainer = map.getContainer();
    const containerWidth = mapContainer.clientWidth;
    const containerHeight = mapContainer.clientHeight;

    // Project the point to screen coordinates
    const point = map.project(lngLat);

    // Determine if point is outside of bounds, considering padding
    const isOutOfBounds =
        point.x < paddingPx ||
        point.x > containerWidth - paddingPx ||
        point.y < paddingPx ||
        point.y > containerHeight - paddingPx;

    // If not out of bounds, return original coordinates
    if (!isOutOfBounds) {
        return {
            clampedLngLat: lngLat,
            isClamped: false,
        };
    }

    // Create a clamped  along the edge of the viewport
    const clampedPoint = {
        x: Math.max(paddingPx, Math.min(containerWidth - paddingPx, point.x)),
        y: Math.max(paddingPx, Math.min(containerHeight - paddingPx, point.y)),
    };

    // Unproject the clamped point back to geographic coordinates
    const clampedLngLat = map.unproject([clampedPoint.x, clampedPoint.y]);

    return {
        clampedLngLat,
        isClamped: true,
    };
}

export function animateAngle({
    from,
    to,
    duration = 300,
    onUpdate,
}: {
    from: number;
    to: number;
    duration?: number;
    onUpdate: (value: number) => void;
}): () => void {
    const startTime = performance.now();
    const normalizedFrom = from % 360;
    const normalizedTo = to % 360;

    let delta = normalizedTo - normalizedFrom;
    if (Math.abs(delta) > 180) {
        delta = delta > 0 ? delta - 360 : delta + 360;
    }

    let frame: number;

    const animate = (now: number) => {
        const elapsed = now - startTime;
        const t = Math.min(elapsed / duration, 1);
        const value = normalizedFrom + delta * t;
        onUpdate(value);

        if (t < 1) {
            frame = requestAnimationFrame(animate);
        } else {
            onUpdate(to);
        }
    };

    animate(performance.now());

    return () => cancelAnimationFrame(frame);
}

export function isValidLngLat(lng: number, lat: number): boolean;
export function isValidLngLat(lngLat: MapLibre.LngLat | null | undefined): lngLat is MapLibre.LngLat;
export function isValidLngLat(lngOrLngLat: number | MapLibre.LngLat | null | undefined, lat?: number): boolean {
    if (typeof lngOrLngLat === 'number') {
        return (
            Number.isFinite(lngOrLngLat) &&
            Number.isFinite(lat) &&
            lngOrLngLat >= -180 &&
            lngOrLngLat <= 180 &&
            (lat as number) >= -90 &&
            (lat as number) <= 90
        );
    }
    return (
        lngOrLngLat != null &&
        typeof lngOrLngLat.lng === 'number' &&
        typeof lngOrLngLat.lat === 'number' &&
        isValidLngLat(lngOrLngLat.lng, lngOrLngLat.lat)
    );
}

export function animateLngLat({
    from,
    to,
    duration = 300,
    onUpdate,
    teleportThreshold = Number.MAX_VALUE,
}: {
    from: MapLibre.LngLat;
    to: MapLibre.LngLat;
    duration?: number;
    onUpdate: (value: MapLibre.LngLat) => void;
    teleportThreshold?: number;
}): () => void {
    if (!isValidLngLat(from) || !isValidLngLat(to)) {
        return () => {};
    }

    if (from.distanceTo(to) > teleportThreshold) {
        onUpdate(to);
        return () => {};
    }
    const start = MapLibre.LngLat.convert(from);
    const end = MapLibre.LngLat.convert(to);
    const startTime = performance.now();

    let frame: number;
    let cancelled = false;

    const animate = (now: number) => {
        if (cancelled) {
            return;
        }

        const elapsed = now - startTime;
        const t = Math.min(elapsed / duration, 1);

        const lng = start.lng + (end.lng - start.lng) * t;
        const lat = start.lat + (end.lat - start.lat) * t;

        if (!isValidLngLat(lng, lat)) {
            return;
        }

        onUpdate(new MapLibre.LngLat(lng, lat));

        if (t < 1) {
            frame = requestAnimationFrame(animate);
        } else {
            onUpdate(end);
        }
    };

    animate(performance.now());

    return () => {
        cancelled = true;
        cancelAnimationFrame(frame);
    };
}

/**
 * Utility functions for constructing map URLs
 */

export interface MapUrlParams {
    titleSlug?: string;
    mapSlug?: string;
    titleId?: string;
    locationId?: string;
    [key: string]: string | undefined;
}

/**
 * Constructs a public web URL for a map
 * @param params - Map URL parameters
 * @param baseUrl - Optional base URL (defaults to window.location.origin)
 * @returns The constructed public map URL
 */
export function createPublicMapUrl(params: MapUrlParams, baseUrl?: string): string {
    const { titleSlug, mapSlug, locationId, ...queryParams } = params;

    const baseSiteUrl = baseUrl || process.env.NEXT_PUBLIC_WAND_WEBSITE_URL;

    if (!titleSlug || !mapSlug) {
        throw new Error('titleSlug and mapSlug are required for public map URLs');
    }

    const url = new URL(`/maps/${titleSlug}/${mapSlug}`, baseSiteUrl);

    if (locationId) {
        url.searchParams.set('location', locationId);
    }

    Object.entries(queryParams).forEach(([key, value]) => {
        if (value) {
            url.searchParams.set(key, value);
        }
    });

    return url.toString();
}

/**
 * Constructs a WeMod protocol URL for opening maps in the Wand app
 * @param params - Map URL parameters
 * @returns The constructed WeMod protocol URL
 */
export function createWemodProtocolUrl(params: MapUrlParams): string {
    const { titleId, mapId, locationId, source, ...queryParams } = params;

    if (!titleId || !mapId) {
        throw new Error('titleId and mapId are required for WeMod protocol URLs');
    }

    const url = new URL(`wemod://maps/${titleId}/${mapId}`);

    if (locationId) {
        url.searchParams.set('location', locationId);
    }

    if (source) {
        url.searchParams.set('source', source);
    }

    Object.entries(queryParams).forEach(([key, value]) => {
        if (value) {
            url.searchParams.set(key, value);
        }
    });

    return url.toString();
}

/**
 * Constructs an iframe embed code for a map
 * @param params - Map URL parameters
 * @param options - Embed options
 * @returns The constructed iframe embed code
 */
export function createMapEmbedCode(
    params: MapUrlParams,
    options: {
        width?: string;
        height?: string;
        style?: string;
        allow?: string;
    } = {},
): string {
    const {
        width = '700',
        height = '500',
        style = 'border-radius: 8px; position: relative; width: 100%; border: none;',
        allow = 'clipboard-write',
    } = options;

    const mapUrl = createPublicMapUrl(params);

    return `<div style="width: ${width}px; height: ${height}px;">
    <iframe style="${style}" src="${mapUrl}" allow="${allow}" width="${width}" height="${height}"></iframe>
        <a style="text-decoration: none; color: #000; font-size: 12px; font-family: sans-serif;" href="${mapUrl}" target="_blank">Wand</a>
    </div>`;
}

export function calculatePlayerLocationDeltas(
    referencePoints: PlayerLocationReferencePoint[],
    gameCoordsMap: MapDataGameCoordinateDeltas['gameCoordsMap'],
): MapDataGameCoordinateDeltas | null {
    if (referencePoints.length < 2) {
        return null;
    }

    const point1 = referencePoints[0];
    const point2 = referencePoints[1];

    if (!point1?.gameCoords || !point2?.gameCoords) {
        return null;
    }

    const xy1 = remapGameCoords(point1.gameCoords, gameCoordsMap);
    const xy2 = remapGameCoords(point2.gameCoords, gameCoordsMap);

    if (!xy1 || !xy2) {
        return null;
    }

    const projectedXY1 = latLonToXYWebMercator(point1.lngLat.lat, point1.lngLat.lng);
    const projectedXY2 = latLonToXYWebMercator(point2.lngLat.lat, point2.lngLat.lng);

    // Calculate scale factors
    const scaleX = (xy2.x - xy1.x) / (projectedXY2.x - projectedXY1.x);
    const scaleY = (xy2.y - xy1.y) / (projectedXY2.y - projectedXY1.y);

    // Calculate offsets
    const offsetX = xy1.x - projectedXY1.x * scaleX;
    const offsetY = xy1.y - projectedXY1.y * scaleY;

    return {
        scaleX,
        scaleY,
        offsetX,
        offsetY,
        gameCoordsMap,
    };
}

/**
 * Convert a lat/lng coordinate to Mercator y. MapLibre renders polygon edges
 * as straight lines in Mercator-projected space, so containment tests must
 * use the same projection to match what's visually rendered on screen.
 */
const toMercatorY = (lat: number): number => {
    const radLat = (lat * Math.PI) / 180;
    return Math.log(Math.tan(Math.PI / 4 + radLat / 2));
};

const isPointInRing = (point: [number, number], ring: ReadonlyArray<[number, number]>): boolean => {
    // Only y (latitude) needs Mercator conversion; x (longitude) is already linear in Mercator projection.
    const [px, py] = [point[0], toMercatorY(point[1])];
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const ri = ring[i] as [number, number];
        const rj = ring[j] as [number, number];
        const [ix, iy] = [ri[0], toMercatorY(ri[1])];
        const [jx, jy] = [rj[0], toMercatorY(rj[1])];
        if (iy > py !== jy > py && px < ((jx - ix) * (py - iy)) / (jy - iy) + ix) {
            inside = !inside;
        }
    }
    return inside;
};

export const isPointInRegion = (point: [number, number], geometry: MapDataRegionGeometry): boolean => {
    if (geometry.type === 'Polygon') {
        const rings = geometry.coordinates as ReadonlyArray<ReadonlyArray<[number, number]>>;
        if (!isPointInRing(point, rings[0] as ReadonlyArray<[number, number]>)) return false;
        for (let i = 1; i < rings.length; i++) {
            if (isPointInRing(point, rings[i] as ReadonlyArray<[number, number]>)) return false;
        }
        return true;
    }

    if (geometry.type === 'MultiPolygon') {
        const polygons = geometry.coordinates as ReadonlyArray<ReadonlyArray<ReadonlyArray<[number, number]>>>;
        return polygons.some(rings => {
            if (!isPointInRing(point, rings[0] as ReadonlyArray<[number, number]>)) return false;
            for (let i = 1; i < rings.length; i++) {
                if (isPointInRing(point, rings[i] as ReadonlyArray<[number, number]>)) return false;
            }
            return true;
        });
    }

    return false;
};
